import { FooterIconProperty } from '../config'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { footerGlyphs } from './glyphs'
import { observer } from 'mobx-react-lite'
import { trainingConstructorStore } from '@pages/TrainingConstructor/stores'
import { useLocation } from 'react-router-dom'
import { ROUTES } from '@shared/constants/routes'
import { coachChatStore, type CoachChatFooterBadgeKind } from '@entities/AIMessenger/model/coachChat.store'
import './footer-badge.css'

interface FooterIconProps {
	iconInfo: FooterIconProperty
	isActive: boolean
	isCurrent: boolean
	onSelect: () => void
}

type RenderedFooterBadge = {
	ariaLabel: string
	kind: 'workouts' | CoachChatFooterBadgeKind
	text?: string
}

export const FooterIcon: FC<FooterIconProps> = observer(({
	iconInfo,
	isActive,
	isCurrent,
	onSelect,
}) => {
	const { t } = useTranslation('common')
	const location = useLocation()
	const Icon = footerGlyphs[iconInfo.icon]
	const title = t(iconInfo.titleKey)

	const shouldShowPendingBadge =
		iconInfo.icon === 'workouts' &&
		location.pathname !== ROUTES.overall.trainingConstructor

	const pendingExercisesCount = shouldShowPendingBadge
		? trainingConstructorStore.addedExercises.length
		: 0
	const chatBadgeKind = iconInfo.icon === 'chat' ? coachChatStore.footerBadgeKind : null
	const currentBadge = useMemo(
		() =>
			getCurrentFooterBadge({
				chatBadgeKind,
				icon: iconInfo.icon,
				pendingExercisesCount,
			}),
		[chatBadgeKind, iconInfo.icon, pendingExercisesCount],
	)

	const [renderedBadge, setRenderedBadge] = useState<RenderedFooterBadge | null>(currentBadge)
	const [isBadgeVisible, setIsBadgeVisible] = useState(Boolean(currentBadge))
	const hideTimerRef = useRef<number | null>(null)
	const workoutsBadge = renderedBadge?.kind === 'workouts' ? renderedBadge : null
	const chatBadge = renderedBadge && renderedBadge.kind !== 'workouts' ? renderedBadge : null

	useEffect(() => {
		if (hideTimerRef.current) {
			window.clearTimeout(hideTimerRef.current)
			hideTimerRef.current = null
		}

		if (currentBadge) {
			setRenderedBadge(currentBadge)
			setIsBadgeVisible(true)
			return
		}

		setIsBadgeVisible(false)
		hideTimerRef.current = window.setTimeout(() => {
			setRenderedBadge(null)
			hideTimerRef.current = null
		}, 190)

		return () => {
			if (hideTimerRef.current) {
				window.clearTimeout(hideTimerRef.current)
				hideTimerRef.current = null
			}
		}
	}, [currentBadge])

	const buttonClassName = [
		'group relative z-10 flex h-[58px] shrink-0 items-center justify-center overflow-visible rounded-[23px] transition-[width,background-color,box-shadow,transform,color] [transition-duration:240ms] [transition-timing-function:cubic-bezier(0.32,0.72,0,1)] active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B9EA25]/45 motion-reduce:transition-none',
		isActive
			? [
				iconInfo.icon === 'workouts' ? 'w-[136px]' : 'w-[128px]',
				'bg-[#F7F8F3]/[0.96] text-[#111315] shadow-[0_8px_18px_rgba(17,19,22,0.045),inset_0_0_0_1px_rgba(22,24,28,0.035),inset_0_1px_0_rgba(255,255,255,0.86)]',
			].join(' ')
			: 'w-[58px] bg-white/[0.34] text-[#747B82] shadow-[inset_0_0_0_1px_rgba(22,24,28,0.035),inset_0_1px_0_rgba(255,255,255,0.68)]',
	].join(' ')

	const labelClassName = [
		'overflow-hidden whitespace-nowrap text-[14px] font-semibold leading-none transition-[max-width,opacity,transform,color,margin-left] [transition-duration:210ms] [transition-timing-function:cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none',
		isActive
			? [
				'ml-[10px] translate-x-0 opacity-100 text-[#17191B]',
				iconInfo.icon === 'workouts' ? 'max-w-[92px]' : 'max-w-[72px]',
			].join(' ')
			: 'max-w-0 -translate-x-1 opacity-0 text-[#747B82]',
	].join(' ')

	return (
		<button
			type="button"
			aria-label={title}
			aria-current={isCurrent ? 'page' : undefined}
			data-active={isActive}
			className={buttonClassName}
			onClick={onSelect}
		>
			<span className="relative flex size-[24px] shrink-0 items-center justify-center">
				<Icon className="size-[22px] opacity-[0.84] transition-[color,opacity,transform] [transition-duration:210ms] [transition-timing-function:cubic-bezier(0.23,1,0.32,1)] group-hover:text-[#2E3337] group-data-[active=true]:scale-[1.05] group-data-[active=true]:text-[#111315] group-data-[active=true]:opacity-100 motion-reduce:transition-none" />

				{chatBadge && (
					<span
						aria-label={chatBadge.ariaLabel}
						data-visible={isBadgeVisible}
						className={`footer-chat-badge footer-chat-badge--${chatBadge.kind}`}
					/>
				)}
			</span>

			{workoutsBadge && (
				<span
					aria-label={workoutsBadge.ariaLabel}
					data-visible={isBadgeVisible}
					className="absolute right-[2px] top-[2px] flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-[#85D600] px-[4px] text-[10px] font-bold leading-none text-white shadow-[0_2px_6px_rgba(133,214,0,0.36)] footer-workouts-badge"
				>
					{workoutsBadge.text}
				</span>
			)}

			<span aria-hidden={!isActive} className={labelClassName}>
				{title}
			</span>
		</button>
	)
})

const getCurrentFooterBadge = ({
	chatBadgeKind,
	icon,
	pendingExercisesCount,
}: {
	chatBadgeKind: CoachChatFooterBadgeKind | null
	icon: FooterIconProperty['icon']
	pendingExercisesCount: number
}): RenderedFooterBadge | null => {
	if (icon === 'workouts' && pendingExercisesCount > 0) {
		return {
			ariaLabel: `${pendingExercisesCount} выбранных упражнений`,
			kind: 'workouts',
			text: String(pendingExercisesCount),
		}
	}

	if (icon !== 'chat' || !chatBadgeKind) {
		return null
	}

	if (chatBadgeKind === 'typing') {
		return { ariaLabel: 'Тренер печатает', kind: 'typing' }
	}
	if (chatBadgeKind === 'action') {
		return { ariaLabel: 'В чате новый ответ', kind: 'action' }
	}
	if (chatBadgeKind === 'error') {
		return { ariaLabel: 'Чат требует внимания', kind: 'error' }
	}
	return { ariaLabel: 'В чате новый ответ', kind: 'answer' }
}
